export const PARTNER_TAB = {
  PARTNERS: "partners",
  INVITE: "invite",
  ELIGIBILITY: "eligibility",
  ACTIVITY: "activity",
};

/** Stable color per partner for cap-table / donut charts. */
export const PARTNER_COLORS = [
  "#64b5f6",
  "#46c479",
  "#ffb74d",
  "#ba68c8",
  "#4dd0e1",
  "#ff8a80",
  "#aed581",
  "#7986cb",
];

export function partnerColor(index) {
  return PARTNER_COLORS[index % PARTNER_COLORS.length];
}

export const formatPartnerCash = (value) =>
  `${Number(value || 0).toLocaleString()} Cash`;

export function computeShareAllocation(members = []) {
  const total = members.reduce((sum, member) => sum + Number(member.estimatedShare || 0), 0);
  if (!members.length) {
    return { total: 0, perPartner: 0, items: [] };
  }
  const perPartner = total > 0 ? total / members.length : 0;
  return {
    total,
    perPartner,
    items: members.map((member, index) => {
      const share = Number(member.estimatedShare || 0);
      return {
        key: String(member.userId),
        label: member.name || "Partner",
        share,
        fraction: total > 0 ? share / total : 1 / members.length,
        isOwner: Boolean(member.isOwner),
        color: partnerColor(index),
      };
    }),
  };
}

export function computePartnerActivityStats(requests = []) {
  const counts = {
    pending: 0,
    accepted: 0,
    expired: 0,
    cancelled: 0,
    rejected: 0,
    removed: 0,
    left: 0,
    other: 0,
  };
  const now = Date.now();
  for (const request of requests) {
    const status = request.status;
    if (status === "pending" && new Date(request.expiresAt).getTime() > now) {
      counts.pending += 1;
      continue;
    }
    if (Object.prototype.hasOwnProperty.call(counts, status)) {
      counts[status] += 1;
    } else {
      counts.other += 1;
    }
  }
  const decided =
    counts.accepted + counts.rejected + counts.expired + counts.cancelled;
  const acceptanceRate = decided > 0 ? counts.accepted / decided : null;
  return { ...counts, acceptanceRate, total: requests.length, decided };
}

export function formatPendingCountdown(expiresAt) {
  if (!expiresAt) return "soon";
  const ms = new Date(expiresAt).getTime() - Date.now();
  if (ms <= 0) return "now";
  const hours = Math.floor(ms / (60 * 60 * 1000));
  if (hours >= 24) {
    const days = Math.floor(hours / 24);
    return `in ${days}d ${hours % 24}h`;
  }
  if (hours >= 1) return `in ${hours}h`;
  const mins = Math.max(1, Math.floor(ms / (60 * 1000)));
  return `in ${mins}m`;
}

export const requestStatusLabel = (status) => {
  if (status === "pending") return "Waiting";
  if (status === "accepted") return "Accepted";
  if (status === "cancelled") return "Cancelled";
  if (status === "expired") return "Expired";
  if (status === "removed") return "Removed";
  if (status === "left") return "Left";
  if (status === "rejected") return "Rejected";
  return status || "Done";
};

export const getRequestInvitedUserId = (request) => {
  const invited = request?.invitedUser;
  if (!invited) return "";
  return String(invited._id || invited);
};

export const countPendingInvites = (requests = []) => {
  const now = Date.now();
  return requests.filter(
    (request) =>
      request.status === "pending" && new Date(request.expiresAt).getTime() > now
  ).length;
};

export const buildLatestRequestsByUser = (requests = []) => {
  const byUser = new Map();
  requests.forEach((request) => {
    const invitedId = getRequestInvitedUserId(request);
    if (!invitedId) return;
    const existing = byUser.get(invitedId);
    const requestTime = new Date(
      request.updatedAt || request.createdAt || request.expiresAt || 0
    ).getTime();
    const existingTime = existing
      ? new Date(
          existing.updatedAt || existing.createdAt || existing.expiresAt || 0
        ).getTime()
      : -1;
    if (!existing || requestTime >= existingTime) {
      byUser.set(invitedId, request);
    }
  });
  return Array.from(byUser.values()).sort(
    (a, b) =>
      new Date(b.updatedAt || b.createdAt || b.expiresAt || 0).getTime() -
      new Date(a.updatedAt || a.createdAt || a.expiresAt || 0).getTime()
  );
};

export const partnerInitials = (name = "") => {
  const parts = String(name).trim().split(/\s+/).filter(Boolean);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return String(name).slice(0, 2).toUpperCase() || "?";
};
